import qs from 'query-string';
import { useCallback, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import type { TableFilter } from 'types/views';

import {
  coerceFilterTypes,
  type FilterValues,
  normalizeFilters,
  removeFilterKeys,
  serializeFiltersToSearch,
  toggleSort,
} from '../filters/tableFilterQuery';

export type FiltersName = Record<string, Record<string, string>>;

// The legacy reset handshake handle the side-menu form patches `.reset` onto.
export type FilterResetHandle = { reset: () => void };

type UseTableFiltersOptions = {
  handleFilters?: (newFilters: FilterValues, currentFilters: FilterValues) => false | FilterValues;
};

const emptyQuickSearchField = () => {
  const input = document.querySelector<HTMLInputElement>("[data-quick-search='1']");
  if (input) input.value = '';
};

/**
 * Owns the table's filter state — active values, the available filter
 * definitions, and the resolved value names — and keeps the active values in
 * sync with the URL. Data fetching stays in the caller, which feeds results in
 * through setAvailableFilters / replaceFiltersName.
 */
export const useTableFilters = ({ handleFilters }: UseTableFiltersOptions = {}) => {
  const history = useHistory();
  const location = useLocation();

  const [filters, setFilters] = useState<FilterValues>(() => ({
    ...coerceFilterTypes(qs.parse(location.search, { arrayFormat: 'comma' })),
    sort_by: '',
  }));

  // Mirror the live filters in a ref so setters that fire back-to-back in the
  // same tick read each other's updates. The PaginationFooter changes page size
  // and resets the page in one handler: without this, setPage would push the URL
  // from the pre-update `filters` closure and clobber the just-applied page_limit.
  const filtersRef = useRef(filters);
  const commitFilters = (next: FilterValues) => {
    filtersRef.current = next;
    setFilters(next);
  };
  const [availableFilters, setAvailableFilters] = useState<TableFilter[]>([]);
  const [filtersName, replaceFiltersName] = useState<FiltersName>({});

  // Legacy reset handshake: the side-menu form patches `.reset` onto this shared
  // handle (kept stable via a ref), which resetFilters then invokes. The clean
  // replacement is a parent-owned Mantine form + form.reset() (see
  // modules/Reminders) — part of the forms migration, not this refactor.
  const filterObject = useRef({ reset: () => {} }).current;

  // Seeds the active values from a raw view/URL bag (e.g. when the selected view
  // changes), coercing types on the way in. Unlike applyFilters this deliberately
  // skips normalizeFilters — seeding from a view must not reset the page or force
  // a page_limit. Stable identity so the caller can list it as an effect dep.
  const seedFilters = useCallback((rawFilters: Record<string, unknown>) => {
    const seeded = coerceFilterTypes(rawFilters);
    filtersRef.current = seeded;
    setFilters(seeded);
  }, []);

  const updateUrl = (nextFilters: FilterValues) => {
    history.push({
      pathname: location.pathname,
      search: serializeFiltersToSearch(nextFilters),
      hash: location.hash,
    });
  };

  const applyFilters = (values: FilterValues) => {
    const currentFilters = filtersRef.current;
    const handledFilters = handleFilters ? handleFilters(values, currentFilters) : values;
    if (!handledFilters) return;

    const nextFilters = normalizeFilters(handledFilters, currentFilters);
    updateUrl(nextFilters);
    commitFilters(nextFilters);
  };

  const setPage = (page: { selected: number }) => {
    updateUrl({ ...filtersRef.current, page: Number(page.selected) + 1 });
  };

  const setPageLimit = (page_limit: number) => () => {
    applyFilters({ ...filtersRef.current, page_limit });
  };

  const submit = (quick_search: string) => {
    const nextFilters = { ...filtersRef.current, quick_search };
    updateUrl(nextFilters);
    commitFilters(nextFilters);
  };

  const setOrder = (field: string) => () => {
    const nextFilters = toggleSort(filtersRef.current, field);
    if (nextFilters) applyFilters(nextFilters);
  };

  const removeFilter = (key: string, extraKey?: string) => {
    if (key === 'quick_search') emptyQuickSearchField();
    applyFilters(removeFilterKeys(filtersRef.current, key, extraKey));
  };

  const resetFilters = () => {
    emptyQuickSearchField();
    filterObject.reset();
    applyFilters({});
  };

  const mergeFilterName = (key: string, values: Record<string, string>) => {
    replaceFiltersName(current => ({ ...current, [key]: values }));
  };

  return {
    filters,
    seedFilters,
    availableFilters,
    setAvailableFilters,
    filtersName,
    replaceFiltersName,
    mergeFilterName,
    filterObject,
    applyFilters,
    setPage,
    setPageLimit,
    submit,
    setOrder,
    removeFilter,
    resetFilters,
  };
};
